// =====================================================================
// Convoyeur.jsx — M-008 (24-06-2026) — page principale, 4 vues internes
// =====================================================================
// Fenêtre Zeus Log (cloud) sur le système de tri de colis (edge).
// Même logique que les autres modules : tuile launcher -> page avec tabs.
//
// Tabs :
//   1. Recherche colis  — par N° suivi / commande / ODP -> photo + détails
//   2. Vue d'ensemble    — lignes de tri (GRAV) + transporteurs + scannés
//   3. Journal           — log des colis (heure, transporteur, ligne...)
//   4. Réconciliation    — à scanner (Effitrace) vs scannés (convoyeur)
// =====================================================================

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { convoyeur as convoyeurApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import Breadcrumbs from '../../components/Breadcrumbs.jsx';
import { tabBtn } from './styles.jsx';
import RechercheColis from './RechercheColis.jsx';
import VueEnsemble from './VueEnsemble.jsx';
import Journal from './Journal.jsx';
import Reconciliation from './Reconciliation.jsx';

const VALID_TABS = ['recherche', 'overview', 'journal', 'reconcile'];

const TAB_LABELS = {
  recherche: 'Recherche colis',
  overview:  "Vue d'ensemble",
  journal:   'Journal',
  reconcile: 'Réconciliation',
};

export default function Convoyeur() {
  const { can } = useAuth();
  const canRead   = can('convoyeur.read.all') || can('convoyeur.manage.all');
  const canManage = can('convoyeur.manage.all');

  const location = useLocation();
  const hashTab = location.hash.replace('#', '');
  const [activeTab, setActiveTab] = useState(VALID_TABS.includes(hashTab) ? hashTab : 'recherche');

  // statut edge (en ligne / hors-ligne)
  const [status, setStatus] = useState(null);
  const refreshStatus = useCallback(() => {
    convoyeurApi.status().then(setStatus).catch(() => setStatus({ online: false, edgeConfigured: false }));
  }, []);
  useEffect(() => {
    refreshStatus();
    const t = setInterval(refreshStatus, 30000);
    return () => clearInterval(t);
  }, [refreshStatus]);

  useEffect(() => {
    if (VALID_TABS.includes(hashTab)) setActiveTab(hashTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  if (!canRead) {
    return (
      <div style={{ padding: 40 }}>
        <h1>Convoyeur</h1>
        <p style={{ color: '#6b7280', marginTop: 16 }}>Permission refusée.</p>
      </div>
    );
  }

  const online = status?.online;

  return (
    <div style={{ padding: '1.5rem 2rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 600 }}>Convoyeur</h1>
        <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>M-008 — tri colis &amp; réconciliation</span>
      </div>
      <p className="hide-mobile" style={{ color: '#6b7280', fontSize: '0.875rem', margin: '0 0 1rem' }}>
        Recherche, suivi et réconciliation des colis triés sur le convoyeur (REVO-TECH).
      </p>

      {/* Bannière statut edge */}
      {status && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '0.5rem 0.875rem', borderRadius: 6, marginBottom: '1rem',
          fontSize: '0.8125rem',
          background: online ? '#ecfdf5' : '#fef3c7',
          color: online ? '#065f46' : '#92400e',
          border: `1px solid ${online ? '#a7f3d0' : '#fde68a'}`,
        }}>
          <span style={{
            width: 9, height: 9, borderRadius: 999,
            background: online ? '#10b981' : '#f59e0b',
            boxShadow: online ? '0 0 0 3px rgba(16,185,129,.18)' : '0 0 0 3px rgba(245,158,11,.18)',
          }} />
          {online ? (
            <span>Convoyeur <strong>en ligne</strong>{status.robot?.parcels != null && <> · {status.robot.parcels} colis chargés (Effitrace)</>}</span>
          ) : (
            <span>
              Convoyeur <strong>hors-ligne</strong> — {status.edgeConfigured
                ? "le host n'est pas joignable pour le moment."
                : "le lien (tunnel) vers le convoyeur n'est pas encore activé."}
            </span>
          )}
        </div>
      )}

      <Breadcrumbs items={[
        { label: 'Convoyeur', onClick: () => setActiveTab('recherche') },
        { label: TAB_LABELS[activeTab] || activeTab },
      ]} />

      <div className="mobile-tabs-scroll" style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '1.5rem' }}>
        <button style={tabBtn(activeTab === 'recherche')} onClick={() => setActiveTab('recherche')}>🔍 Recherche colis</button>
        <button style={tabBtn(activeTab === 'overview')} onClick={() => setActiveTab('overview')}>📊 Vue d'ensemble</button>
        <button style={tabBtn(activeTab === 'journal')} onClick={() => setActiveTab('journal')}>📋 Journal</button>
        <button style={tabBtn(activeTab === 'reconcile')} onClick={() => setActiveTab('reconcile')}>📦 Réconciliation</button>
      </div>

      {activeTab === 'recherche' && <RechercheColis />}
      {activeTab === 'overview'  && <VueEnsemble />}
      {activeTab === 'journal'   && <Journal />}
      {activeTab === 'reconcile' && <Reconciliation canManage={canManage} onRobotDone={refreshStatus} />}
    </div>
  );
}
